import { cx } from "@/lib/cx.js";
import { PRODUCTIVITY, ROLES, VERSION_TIERS, toneOf } from "@/lib/tokens.js";

/*
 * <Tag tone="emerald">Active</Tag>
 * <Tag tone="rose" onRemove={fn}>Role: Admin</Tag>      removable filter chip
 * <Tag dot tone="amber">Late</Tag>                      with a leading dot
 *
 * The three thin wrappers below exist so a status is never re-mapped to a colour
 * at the call site — that is how the same state ends up two different colours on
 * two different pages.
 */
export default function Tag({ tone = "slate", dot = false, onRemove, className, children, ...rest }) {
    const t = toneOf(tone);

    return (
        <span className={cx("tag", t.tag, onRemove && "h-6 gap-1.5 pr-1", className)} {...rest}>
            {dot && <span className={cx("size-1.5 shrink-0 rounded-full", t.dot)} />}
            {children}
            {onRemove && (
                <button
                    type="button"
                    onClick={onRemove}
                    aria-label="Remove filter"
                    className="grid size-4 place-items-center rounded-sm transition hover:bg-black/10 dark:hover:bg-white/15"
                >
                    <svg
                        className="size-3"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="3"
                        strokeLinecap="round"
                    >
                        <path d="M18 6 6 18M6 6l12 12" />
                    </svg>
                </button>
            )}
        </span>
    );
}

export function ProductivityTag({ status, ...rest }) {
    const p = PRODUCTIVITY[status] ?? PRODUCTIVITY.unrated;
    return (
        <Tag tone={p.tone} {...rest}>
            {p.label}
        </Tag>
    );
}

export function RoleTag({ role, ...rest }) {
    const r = ROLES[role];
    return (
        <Tag tone={r?.tone ?? "slate"} {...rest}>
            {r?.label ?? role}
        </Tag>
    );
}

export function VersionTag({ tier, ...rest }) {
    const v = VERSION_TIERS[tier] ?? VERSION_TIERS.none;
    return (
        <Tag tone={v.tone} {...rest}>
            {v.label}
        </Tag>
    );
}

/** Active / Inactive, with the dot the markup used in the users table. */
export function StatusDot({ active, labels = ["Active", "Inactive"] }) {
    return (
        <span className="flex items-center gap-1.5">
            <span
                className={cx("size-2 shrink-0 rounded-full", active ? "bg-emerald-500" : "bg-rose-500")}
            />
            <span className={active ? undefined : "text-slate-400 dark:text-slate-500"}>
                {active ? labels[0] : labels[1]}
            </span>
        </span>
    );
}
